import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ASSET_STATUSES = ["Received", "Pending Audit", "In Transit", "Data Destroyed", "Recycled", "Resold"];
const LIMIT = 15;

export default function AdminAssets() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-assets", page, search],
    queryFn: async () => {
      const { data } = await api.get("/admin/assets", {
        params: { skip: page * LIMIT, limit: LIMIT, search },
      });
      return data;
    },
    keepPreviousData: true,
  });

  const mutation = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/assets/${id}`, { status }),
    onSuccess: (res) => {
      toast.success(`${res.data.tag} → ${res.data.status}`);
      qc.invalidateQueries({ queryKey: ["admin-assets"] });
    },
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"),
  });

  const assets = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div data-testid="admin-assets-page">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-2">/ Admin</p>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Manage Assets</h1>
      </div>

      <div className="flex items-center gap-2 border border-border px-3 bg-card mb-4 max-w-md">
        <Search size={16} className="text-muted-foreground" />
        <input
          data-testid="admin-asset-search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="SEARCH TAG OR CATEGORY"
          className="bg-transparent py-3 w-full font-mono text-xs uppercase tracking-wider placeholder:text-muted-foreground/60 focus:outline-none"
        />
      </div>

      <div className="border border-border overflow-x-auto thin-scroll">
        <table className="w-full text-left border-collapse min-w-[720px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {["Asset Tag", "Category", "Location", "Status"].map((h) => (
                <th key={h} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center font-mono text-xs text-muted-foreground">Loading…</td></tr>
            ) : assets.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center font-mono text-xs text-muted-foreground">No assets found.</td></tr>
            ) : assets.map((a) => (
              <tr key={a.id} data-testid="admin-asset-row" className="border-b border-border hover:bg-muted/30">
                <td className="px-4 py-3 font-mono text-sm text-primary">{a.tag}</td>
                <td className="px-4 py-3 font-sans text-sm">{a.category}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.location}</td>
                <td className="px-4 py-3">
                  <Select value={a.status} onValueChange={(v) => mutation.mutate({ id: a.id, status: v })}>
                    <SelectTrigger data-testid={`asset-status-${a.tag}`} className="w-[160px] rounded-none border-border bg-card h-9 font-mono text-[11px] uppercase tracking-wider focus:ring-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {ASSET_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="rounded-none font-mono text-[11px] uppercase tracking-wider">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="font-mono text-[11px] text-muted-foreground">Page {page + 1} of {pages} · {total} assets</span>
        <div className="flex gap-2">
          <button data-testid="assets-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="flex items-center gap-1 border border-border px-3 py-2 font-mono text-[11px] uppercase disabled:opacity-40 hover:border-primary transition-colors">
            <ChevronLeft size={14} /> Prev
          </button>
          <button data-testid="assets-next" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 border border-border px-3 py-2 font-mono text-[11px] uppercase disabled:opacity-40 hover:border-primary transition-colors">
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
